import { Link, useNavigate } from "@tanstack/react-router";
import { Building2, ClipboardList, Clock, FileText, ImageOff, Search, Star, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { HomeFavoritesDrawer } from "@/components/home/HomeFavoritesDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { assetContentUrl, useBranding } from "@/hooks/use-branding";
import { useClaimRestrictionCheck } from "@/hooks/use-claim";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  useSearchAgencies,
  useSearchClaims,
  useSearchPolicies,
  useSearchTypes,
} from "@/hooks/use-search";
import { useUpdateUserDisplay, useUserDisplay } from "@/hooks/use-user-display";
import {
  addRecentSearch,
  clearRecentSearches,
  type RecentSearchKind,
  useRecentSearches,
} from "@/lib/recent-searches";
import { cn } from "@/lib/utils";
import { ASSET_SLUGS } from "@/schemas/branding";

// Home-page brand hero. Shows the admin-uploaded logo (Config → Branding) when
// present; otherwise a neutral placeholder prompting an upload. While the
// metadata loads we render nothing to avoid a flash of the placeholder before
// a logo resolves.
function HomeBrandHero() {
  const { data, isLoading } = useBranding();
  const logo = data?.logo ?? null;

  if (isLoading) {
    return <div className="h-16" aria-hidden="true" />;
  }

  if (logo) {
    const base = assetContentUrl(ASSET_SLUGS.homepageLogo);
    const src = `${base}${base.includes("?") ? "&" : "?"}v=${encodeURIComponent(logo.updatedOn)}`;
    return (
      <div className="flex justify-center pb-4 pt-1">
        <img src={src} alt="Home logo" className="max-h-20 max-w-full object-contain" />
      </div>
    );
  }

  return (
    <div className="pb-4 pt-1">
      <div className="mx-auto flex max-w-md flex-col items-center gap-2 rounded-md border border-dashed bg-muted/30 px-6 py-6 text-center text-muted-foreground">
        <ImageOff className="size-7" aria-hidden="true" />
        <span className="text-sm">
          No logo set. An admin can upload one under Configuration → Branding.
        </span>
      </div>
    </div>
  );
}

// Icon + label per recent-search kind. The inline Recent block under the
// search box uses these to render mixed-kind rows in one flat list.
const RECENT_KIND_META: Record<
  RecentSearchKind,
  { label: string; icon: typeof FileText; to: string }
> = {
  policy: { label: "Policy", icon: FileText, to: "/policy/$policyNumber" },
  claim: { label: "Claim", icon: ClipboardList, to: "/claims/$claimNumber" },
  agency: { label: "Agency", icon: Building2, to: "/agency/$agentNumber" },
};

type SearchCategory = "Policy Number" | "Agency Number" | "Claim Number";

const SEARCH_TABS: { key: SearchCategory; label: string; icon: typeof FileText }[] = [
  { key: "Policy Number", label: "Policy", icon: FileText },
  { key: "Claim Number", label: "Claim", icon: ClipboardList },
  { key: "Agency Number", label: "Agency", icon: Building2 },
];

// Legacy projection bundles "<NUMBER> - <NAME> (<STATUS>)?" into one column.
// Strip the leading "<NUMBER> - " prefix so the home-page table can render
// name and number side by side.
function splitLabel(label: string, number: string): { name: string; suffix: string } {
  const prefix = `${number} - `;
  const tail = label.startsWith(prefix) ? label.slice(prefix.length) : label;
  // Pull a trailing "(...)" status suffix out so it can be rendered subtly.
  const match = tail.match(/^(.*?)(\s*\(([^)]+)\))?$/);
  if (!match) return { name: tail.trim(), suffix: "" };
  return { name: (match[1] ?? "").trim(), suffix: (match[3] ?? "").trim() };
}

export function HomePage() {
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState<SearchCategory>("Policy Number");
  const [favoritesDrawerOpen, setFavoritesDrawerOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(searchText, 300);

  // UserDisplay sync. Hydrate from the user's last selection. Re-hydrate
  // whenever fresh data arrives until the user starts interacting — TanStack
  // Query returns the cached value first and refetches in the background, so
  // hydrating only on first render leaves us showing stale data after the user
  // navigates back from a dashboard.
  const { data: userDisplay } = useUserDisplay();
  const { data: searchTypes } = useSearchTypes();
  const { mutate: mutateUserDisplay } = useUpdateUserDisplay();
  const userInteracted = useRef(false);
  const recent = useRecentSearches();
  // Cached claim names in localStorage can go stale if a claim is restricted
  // from the user after they last viewed it. Re-validate on every home-page
  // mount and overlay "Restricted" for any claim that's now off-limits.
  const recentClaimNumbers = recent
    .filter((item) => item.kind === "claim")
    .map((item) => item.number);
  const restrictionCheck = useClaimRestrictionCheck(recentClaimNumbers);
  const restrictedClaimSet = new Set(restrictionCheck.data?.restricted ?? []);

  // Inline Recent list mirrors the active search category — clicking the
  // "Policy" tab hides the recent claim/agency rows and vice versa. Keeps
  // the list focused on what the user is currently exploring.
  const activeRecentKind: RecentSearchKind =
    selectedType === "Policy Number"
      ? "policy"
      : selectedType === "Claim Number"
        ? "claim"
        : "agency";
  const filteredRecent = recent.filter((item) => item.kind === activeRecentKind);
  const recentHeaderLabel =
    activeRecentKind === "policy"
      ? "Recent policies"
      : activeRecentKind === "claim"
        ? "Recent claims"
        : "Recent agencies";

  useEffect(() => {
    if (userInteracted.current) return;
    if (!userDisplay || !searchTypes) return;

    let initialTab: SearchCategory = "Policy Number";
    if (userDisplay.searchTypeId) {
      const matchedType = searchTypes.find((t) => t.id === userDisplay.searchTypeId);
      if (
        matchedType?.searchType === "Policy Number" ||
        matchedType?.searchType === "Agency Number" ||
        matchedType?.searchType === "Claim Number"
      ) {
        initialTab = matchedType.searchType;
      }
    }
    setSelectedType(initialTab);

    const remembered =
      initialTab === "Policy Number"
        ? userDisplay.policyNumber
        : initialTab === "Claim Number"
          ? userDisplay.claimNumber
          : userDisplay.agencyNumber;
    setSearchText(remembered ?? "");
  }, [userDisplay, searchTypes]);

  const searchTypeIdFor = (key: SearchCategory): string | null =>
    searchTypes?.find((t) => t.searchType === key)?.id ?? null;

  const isPolicySearch = selectedType === "Policy Number";
  const isAgencySearch = selectedType === "Agency Number";
  const isClaimSearch = selectedType === "Claim Number";

  const { data: policies, isLoading: policiesLoading } = useSearchPolicies(
    isPolicySearch ? debouncedSearch : "",
  );
  const { data: agencies, isLoading: agenciesLoading } = useSearchAgencies(
    isAgencySearch ? debouncedSearch : "",
  );
  const { data: claims, isLoading: claimsLoading } = useSearchClaims(
    isClaimSearch ? debouncedSearch : "",
  );

  const isLoading = policiesLoading || agenciesLoading || claimsLoading;
  const hasResults =
    (isPolicySearch && policies && policies.length > 0) ||
    (isAgencySearch && agencies && agencies.length > 0) ||
    (isClaimSearch && claims && claims.length > 0);

  const placeholders: Record<SearchCategory, string> = {
    "Policy Number": "Search by policy number (e.g. WC-001000)...",
    "Agency Number": "Search by agency number (e.g. AG-0100)...",
    "Claim Number": "Search by claim number (e.g. CLM-005)...",
  };

  // Legacy projections only return (number, NumberName) — no insured/agency
  // name field. The names are still embedded in the label for display, but we
  // no longer write policyholderName / agencyName to UserDisplay on click.
  // This is reversible when the rich projection comes back post-validation.
  const selectPolicy = (policyNumber: string, displayName: string | null) => {
    setSearchText(policyNumber);
    mutateUserDisplay({
      policyNumber,
      searchText: policyNumber,
      searchTypeId: searchTypeIdFor("Policy Number"),
    });
    addRecentSearch({ kind: "policy", number: policyNumber, name: displayName });
  };
  const selectAgency = (agencyNumber: string, displayName: string | null) => {
    setSearchText(agencyNumber);
    mutateUserDisplay({
      agencyNumber,
      searchText: agencyNumber,
      searchTypeId: searchTypeIdFor("Agency Number"),
    });
    addRecentSearch({ kind: "agency", number: agencyNumber, name: displayName });
  };
  const selectClaim = (claimNumber: string, displayName: string | null) => {
    setSearchText(claimNumber);
    mutateUserDisplay({
      claimNumber,
      searchText: claimNumber,
      searchTypeId: searchTypeIdFor("Claim Number"),
    });
    addRecentSearch({ kind: "claim", number: claimNumber, name: displayName });
  };

  // Enter opens the exact-number match if there is one, otherwise the top
  // result. Search is the app's front door — type a number, hit Enter, land
  // on the dashboard without reaching for the mouse.
  const navigate = useNavigate();
  const openTopResult = () => {
    const query = searchText.trim().toUpperCase();
    if (!query) return;
    if (isPolicySearch && policies && policies.length > 0) {
      const hit = policies.find((p) => p.policyNumber.toUpperCase() === query) ?? policies[0];
      if (!hit) return;
      const { name } = splitLabel(hit.numberName, hit.policyNumber);
      selectPolicy(hit.policyNumber, name || null);
      navigate({ to: "/policy/$policyNumber", params: { policyNumber: hit.policyNumber } });
    } else if (isAgencySearch && agencies && agencies.length > 0) {
      const hit = agencies.find((a) => a.agentNumber.toUpperCase() === query) ?? agencies[0];
      if (!hit) return;
      const { name } = splitLabel(hit.nameNumber, hit.agentNumber);
      selectAgency(hit.agentNumber, name || null);
      navigate({ to: "/agency/$agentNumber", params: { agentNumber: hit.agentNumber } });
    } else if (isClaimSearch && claims && claims.length > 0) {
      const hit = claims.find((c) => c.claimNumber.toUpperCase() === query) ?? claims[0];
      if (!hit) return;
      const { name } = splitLabel(hit.nameNumber, hit.claimNumber);
      selectClaim(hit.claimNumber, name || null);
      navigate({ to: "/claims/$claimNumber", params: { claimNumber: hit.claimNumber } });
    }
  };

  // Focus the search box on mount — the home page is search-first, and the
  // demo flow is "land, type a number, Enter."
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  return (
    <div>
      {/* Brand hero — admin-uploaded logo or a placeholder (Config → Branding).
          Kept tight so the search box stays above the fold on laptop screens. */}
      <HomeBrandHero />

      <div className="mx-auto max-w-3xl space-y-6">
        <h2 className="font-medium text-foreground text-base">Explore</h2>

        {/* Category toggle buttons + Favorites pill. Favorites is pushed to
            the right (ml-auto) and styled in the MEMIC secondary gold so it
            reads as a distinct action — not another search-type tab. Server-
            side UserDisplay update is gated on "user has typed something
            into the box for this tab" — bare tab clicks no longer generate
            audit traffic. */}
        <div
          role="tablist"
          aria-label="Search category"
          className="flex items-center gap-2"
          onKeyDown={(e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            e.preventDefault();
            const idx = SEARCH_TABS.findIndex((t) => t.key === selectedType);
            const offset = e.key === "ArrowLeft" ? -1 : 1;
            const next = SEARCH_TABS[(idx + offset + SEARCH_TABS.length) % SEARCH_TABS.length];
            if (!next) return;
            userInteracted.current = true;
            setSelectedType(next.key);
            setSearchText("");
          }}
        >
          {SEARCH_TABS.map((tab) => {
            const isActive = selectedType === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => {
                  userInteracted.current = true;
                  setSelectedType(tab.key);
                  setSearchText("");
                }}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "border-primary bg-primary text-primary-foreground shadow-sm"
                    : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setFavoritesDrawerOpen(true)}
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 font-medium text-foreground text-sm transition-colors hover:bg-accent"
          >
            <Star className="h-4 w-4 fill-amber-400 text-amber-500" aria-hidden="true" />
            Favorites
          </button>
        </div>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder={placeholders[selectedType]}
            value={searchText}
            onChange={(e) => {
              userInteracted.current = true;
              setSearchText(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") openTopResult();
            }}
            className="pl-9 h-11 text-base bg-card"
          />
        </div>

        {/* Recent searches — filtered to the active category tab so the list
            stays focused on what the user is currently exploring. Hidden when
            the search box has content (live results take priority) and when
            there are no recents for this kind yet. Favorites live in the
            slide-out drawer (top-right Favorites button). */}
        {debouncedSearch.trim().length === 0 && filteredRecent.length > 0 && (
          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b px-4 py-2">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Clock className="h-4 w-4" />
                <span className="font-medium">{recentHeaderLabel}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-muted-foreground text-xs hover:text-foreground"
                onClick={() => clearRecentSearches()}
                title="Clear recent searches across all kinds"
              >
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            </div>
            <ul>
              {filteredRecent.map((item) => {
                const meta = RECENT_KIND_META[item.kind];
                const Icon = meta.icon;
                const isRestricted = item.kind === "claim" && restrictedClaimSet.has(item.number);
                const linkProps =
                  item.kind === "policy"
                    ? ({
                        to: "/policy/$policyNumber",
                        params: { policyNumber: item.number },
                      } as const)
                    : item.kind === "agency"
                      ? ({
                          to: "/agency/$agentNumber",
                          params: { agentNumber: item.number },
                        } as const)
                      : ({
                          to: "/claims/$claimNumber",
                          params: { claimNumber: item.number },
                        } as const);
                const onActivate = () => {
                  if (item.kind === "policy") selectPolicy(item.number, item.name);
                  else if (item.kind === "agency") selectAgency(item.number, item.name);
                  else selectClaim(item.number, item.name);
                };
                return (
                  <li key={`${item.kind}-${item.number}`}>
                    <Link
                      {...linkProps}
                      onClick={onActivate}
                      className="flex items-center gap-3 border-b px-4 py-2 text-sm last:border-0 hover:bg-muted/30"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium text-primary">
                        {isRestricted ? "Restricted" : item.number}
                      </span>
                      {!isRestricted && item.name && (
                        <span className="truncate text-muted-foreground">{item.name}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Results */}
        {debouncedSearch.trim().length > 0 && (
          <div className="rounded-lg border bg-card">
            {isLoading && (
              <div className="space-y-2 p-3">
                {["sk-0", "sk-1", "sk-2"].map((k) => (
                  <div key={k} className="h-10 animate-pulse rounded bg-muted/40" />
                ))}
              </div>
            )}

            {!isLoading && !hasResults && (
              <div className="p-8 text-center text-muted-foreground">
                No results found for &ldquo;{debouncedSearch}&rdquo;.
              </div>
            )}

            {/* Policy results — split combined NumberName label into separate
                Number / Name columns. */}
            {isPolicySearch && policies && policies.length > 0 && (
              <div>
                <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Policies ({policies.length})</span>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Policy Number</th>
                      <th className="px-4 py-2 font-medium">Insured Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policies.map((p) => {
                      const { name, suffix } = splitLabel(p.numberName, p.policyNumber);
                      return (
                        <tr
                          key={p.policyNumber}
                          className="border-b last:border-0 hover:bg-muted/30"
                        >
                          <td className="px-4 py-2 font-medium text-sm">
                            <Link
                              to="/policy/$policyNumber"
                              params={{ policyNumber: p.policyNumber }}
                              onClick={() => selectPolicy(p.policyNumber, name || null)}
                              className="text-primary hover:underline"
                            >
                              {p.policyNumber}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span>{name || "—"}</span>
                            {suffix && (
                              <span className="ml-1 text-muted-foreground text-xs">({suffix})</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Agency results — split combined NameNumber label. */}
            {isAgencySearch && agencies && agencies.length > 0 && (
              <div>
                <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Agencies ({agencies.length})</span>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Agency Number</th>
                      <th className="px-4 py-2 font-medium">Agency Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agencies.map((a) => {
                      const { name } = splitLabel(a.nameNumber, a.agentNumber);
                      return (
                        <tr
                          key={a.agentNumber}
                          className="border-b last:border-0 hover:bg-muted/30"
                        >
                          <td className="px-4 py-2 font-medium text-sm">
                            <Link
                              to="/agency/$agentNumber"
                              params={{ agentNumber: a.agentNumber }}
                              onClick={() => selectAgency(a.agentNumber, name || null)}
                              className="text-primary hover:underline"
                            >
                              {a.agentNumber}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm">{name || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Claim results — split combined NameNumber label. */}
            {/* (Favorites panel renders below this whole search results block.) */}
            {isClaimSearch && claims && claims.length > 0 && (
              <div>
                <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2">
                  <ClipboardList className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Claims ({claims.length})</span>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Claim Number</th>
                      <th className="px-4 py-2 font-medium">Claimant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((c) => {
                      const { name, suffix } = splitLabel(c.nameNumber, c.claimNumber);
                      return (
                        <tr
                          key={c.claimNumber}
                          className="border-b last:border-0 hover:bg-muted/30"
                        >
                          <td className="px-4 py-2 font-medium text-sm">
                            <Link
                              to="/claims/$claimNumber"
                              params={{ claimNumber: c.claimNumber }}
                              onClick={() => selectClaim(c.claimNumber, name || null)}
                              className="text-primary hover:underline"
                            >
                              {c.claimNumber}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span>{name || "—"}</span>
                            {suffix && (
                              <span className="ml-1 text-muted-foreground text-xs">({suffix})</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <HomeFavoritesDrawer open={favoritesDrawerOpen} onOpenChange={setFavoritesDrawerOpen} />
    </div>
  );
}
